import { For, Form, FormApi, If, ValidateRules, onMount, watch } from 'cdui-js';
import { CheckBoxCard } from '../../../../components/CheckBoxCard';
import { busConfigs, busConfigsTick } from '../../../../../../stores/bus-config';
import { lastProcess, nextProcess, pState, setFormApi } from '../../store';
import { LastNextBtns } from '../../components/LastNextBtns';
import { saveAccount, saveApply } from '../../../../../../api/open-account';
import { OpenFormItem } from '../../components/OpenFormItem';
import i18n from '../../../../../../i18n';
import { isOpenProgressReEdit } from '../../reEditMode';

const HK_MARGIN_ACCOUNT = 'HK_MARGIN_ACCOUNT';
const HK_CASH_ACCOUNT = 'HK_CASH_ACCOUNT';
/** 证券账户展示顺序：孖展第一，现金第二 */
const SECURITIES_DISPLAY_ORDER = [HK_MARGIN_ACCOUNT, HK_CASH_ACCOUNT];

const sortSecuritiesList = <T extends { value: string }>(list: T[]) =>
  [...list].sort((a, b) => {
    const ai = SECURITIES_DISPLAY_ORDER.indexOf(a.value);
    const bi = SECURITIES_DISPLAY_ORDER.indexOf(b.value);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

/** 衍生品买卖：接口 code 或文案（本地静态字典可能无此项） */
const isDerivativeOption = (item: { value?: string; label?: string }) => {
  const value = String(item.value || '');
  const label = String(item.label || '');
  if (/DERIV/i.test(value)) return true;
  if (/衍生|Derivative/i.test(label)) return true;
  if (label === i18n.OpenProgress.Info.Investment.IntendTradeDerivative) return true;
  return false;
};

/** 开户前端不展示 A股通（code=CHINA_CONNECT），衍生产品保留 */
export const isChinaConnectOption = (item: { value?: string; label?: string }) => {
  const value = String(item.value || '');
  const label = String(item.label || '');
  if (value === 'CHINA_CONNECT') return true;
  if (/A股通|China Connect/i.test(label)) return true;
  return false;
};

/** 投资经验页「衍生产品买卖」意向：'0' 无意 / '1' 有意 */
const getIntendTradeDerivative = () =>
  String(pState.investmentExpForm.intendTradeDerivative || '');

/** 已在投资经验页选定意向时，账户页衍生品勾选由意向锁定，不可改 */
const isDerivativeChoiceLocked = () => {
  const v = getIntendTradeDerivative();
  return v === '0' || v === '1';
};

/**
 * 提交用：有意→必须开权限；无意→必须不开；未锁定时仍看勾选结果。
 */
export const resolveNeedDerivativeTradePermission = (tradeCheckedCount: number) => {
  const v = getIntendTradeDerivative();
  if (v === '1') return true;
  if (v === '0') return false;
  return tradeCheckedCount > 0;
};

/** 按意向决定衍生品是否勾选；无意向时返回 null 表示不强制 */
const forcedDerivativeChecked = (): boolean | null => {
  const v = getIntendTradeDerivative();
  if (v === '1') return true;
  if (v === '0') return false;
  return null;
};

const isSecuritiesAccountType = (value: string) =>
  value === HK_MARGIN_ACCOUNT || value === HK_CASH_ACCOUNT;

/** 融资（孖展）与现金互斥：选中其一并取消另一 */
const selectExclusiveSecuritiesAccount = (
  list: { value: string; check: boolean }[],
  value: string,
) => {
  list.forEach((i) => {
    if (isSecuritiesAccountType(i.value)) {
      i.check = i.value === value;
    }
  });
};

const getRules = (): ValidateRules => ({
  securitiesList: {
    type: 'object',
    onvalidate(target) {
      const list = target.value as any[];
      // 融资 / 现金二选一，至少选一种
      const hasMarginOrCash = list.some(
        (i) => i.check && isSecuritiesAccountType(i.value),
      );
      if (!hasMarginOrCash) {
        return i18n.OpenProgress.Info.Account.ValidateSecurities;
      }
    },
  },
  fundList: {
    type: 'object',
    onvalidate(target) {
      const list = target.value as any[];
      if (!list.find((i) => i.check)) {
        return i18n.OpenProgress.Info.Account.ValidateFund;
      }
    },
  },
});

/** value+check 签名；一致则跳过赋新数组，避免 equals:false 下同步重入炸栈 */
const listOptionSignature = (list: { value?: string; check?: boolean }[]) =>
  (list || []).map((i) => `${i?.value}:${i?.check ? 1 : 0}`).join(',');

export const SelectAccount = (props) => {
  let formApi: FormApi = null;
  const rules = getRules();
  /** 写入列表会同步刷新 For；重建过程中禁止重入 */
  let rebuildingLists = false;

  /** 从字典重建勾选列表；保留已有勾选，字典晚到时补齐选项 */
  const rebuildListsFromBusConfigs = () => {
    if (rebuildingLists) return;
    rebuildingLists = true;
    try {
      rebuildListsFromBusConfigsBody();
    } finally {
      rebuildingLists = false;
    }
  };

  const rebuildListsFromBusConfigsBody = () => {
    const form = pState.selectAccountForm;
    const prevSec = form.securitiesList || [];
    const prevFund = form.fundList || [];
    const prevTrade = form.tradePermissionList || [];
    const isEditSecurities = !!form.securitiesAccountType;
    const isEditTrade = form.needDerivativeTradePermission === true;
    const forcedDeriv = forcedDerivativeChecked();

    const nextSec = sortSecuritiesList(
      (busConfigs.securitiesAccountType || [])
        .filter((i) => i.value !== 'CRYPTO_ACCOUNT')
        .map((item) => {
          const prev = prevSec.find((p) => p.value === item.value);
          let check: boolean;
          if (isDerivativeOption(item) && forcedDeriv !== null) {
            // 投资经验页已选定意向：强制勾/不勾
            check = forcedDeriv;
          } else if (prev) {
            check = !!prev.check;
          } else if (isEditSecurities) {
            check = form.securitiesAccountType.includes(item.value);
          } else {
            // 新开：默认孖展；证券列表里的衍生品项不在此处默认勾（走交易权限区）
            check = item.value === HK_MARGIN_ACCOUNT;
          }
          return {
            ...item,
            check,
          };
        }),
    );
    const marginItem = nextSec.find((i) => i.value === HK_MARGIN_ACCOUNT);
    const cashItem = nextSec.find((i) => i.value === HK_CASH_ACCOUNT);
    if (marginItem?.check && cashItem?.check) {
      cashItem.check = false;
    }

    const nextFund = (busConfigs.fundAccountType || []).map((item) => {
      const prev = prevFund.find((p) => p.value === item.value);
      return {
        ...item,
        check: prev ? !!prev.check : form.fundAccountType.includes(item.value),
      };
    });

    const nextTrade = (busConfigs.tradePermission || [])
      .filter((item) => !isChinaConnectOption(item))
      .map((item) => {
        const prev = prevTrade.find((p) => p.value === item.value);
        return {
          ...item,
          check:
            forcedDeriv !== null ? forcedDeriv : prev ? !!prev.check : isEditTrade,
        };
      });

    // 与 ActivateEleServices 同约定：签名不变不写，切断 If/Switch 外层同步重入
    if (listOptionSignature(nextSec) !== listOptionSignature(prevSec)) {
      form.securitiesList = nextSec;
    }
    if (listOptionSignature(nextFund) !== listOptionSignature(prevFund)) {
      form.fundList = nextFund;
    }
    if (listOptionSignature(nextTrade) !== listOptionSignature(prevTrade)) {
      form.tradePermissionList = nextTrade;
    }

    if (forcedDeriv !== null && form.needDerivativeTradePermission !== forcedDeriv) {
      form.needDerivativeTradePermission = forcedDeriv;
    }
  };

  // 勿在组件体/渲染期写 pState.list（新数组会触发再渲染并可能栈溢出）；与 ActivateEleServices 同约定
  onMount(() => {
    rebuildListsFromBusConfigs();
  });
  watch(
    () => busConfigsTick.v,
    () => {
      const secLen = (busConfigs.securitiesAccountType || []).length;
      if (secLen > 0) {
        rebuildListsFromBusConfigs();
      }
    },
  );

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="font-s-L font-bold color-D margin-b-L">
        {i18n.OpenProgress.Info.Account.Title}
        {props.stepText}
      </div>

      <Form
        data={pState.selectAccountForm}
        rules={rules}
        align="top"
        api={(api) => {
          formApi = api;
          setFormApi(api);
        }}
      >
        <OpenFormItem label="" field="securitiesList" errorField="securitiesAccountType">
          <div class="margin-b-L">
            <div class="color-D font-s flex align-center gap-4px margin-b-L">
              {i18n.OpenProgress.Info.Account.SecuritiesAccount}
            </div>

            <div class="flex flex-wrap gap-12px">
              <For each={pState.selectAccountForm.securitiesList}>
                {(item) => {
                  const derivLocked =
                    isDerivativeOption(item) && isDerivativeChoiceLocked();
                  return (
                    <CheckBoxCard
                      active={item.check}
                      style={{
                        width: '48%',
                        height: '44px',
                        ...(derivLocked
                          ? { opacity: '0.65', cursor: 'not-allowed' }
                          : {}),
                      }}
                      class="flex align-center justify-center"
                      onclick={() => {
                        // 融资 / 现金：二选一（点已选项不取消，需点另一项切换）
                        if (isSecuritiesAccountType(item.value)) {
                          if (item.check) return;
                          selectExclusiveSecuritiesAccount(
                            pState.selectAccountForm.securitiesList,
                            item.value,
                          );
                          return;
                        }
                        // 衍生品：意向已选定则禁止改勾选
                        if (derivLocked) return;
                        item.check = !item.check;
                      }}
                    >
                      <div class={derivLocked ? 'color-L' : 'color-D'}>{item.label}</div>
                    </CheckBoxCard>
                  );
                }}
              </For>
            </div>
          </div>
        </OpenFormItem>

        <div class="margin-b-L">
          <div class="color-D font-s flex align-center gap-4px margin-b-L">
            {i18n.OpenProgress.Info.Account.TradePermission}
          </div>
          <div class="flex flex-wrap gap-12px">
            <For each={pState.selectAccountForm.tradePermissionList}>
              {(item) => {
                const locked = isDerivativeChoiceLocked();
                return (
                  <CheckBoxCard
                    active={item.check}
                    style={{
                      width: '48%',
                      height: '44px',
                      ...(locked ? { opacity: '0.65', cursor: 'not-allowed' } : {}),
                    }}
                    class="flex align-center justify-center"
                    onclick={() => {
                      // 投资经验页已选定意向：强制勾/不勾，禁止用户改
                      if (locked) return;
                      item.check = !item.check;
                    }}
                  >
                    <div class={locked ? 'color-L' : 'color-D'}>{item.label}</div>
                  </CheckBoxCard>
                );
              }}
            </For>
          </div>
        </div>
      </Form>

      <If when={!isOpenProgressReEdit(props)}>
        <LastNextBtns
          last={() => {
            lastProcess();
          }}
          next={async () => {
            const valid = await formApi.validate();
            if (!valid) {
              return;
            }
            const securSelect = pState.selectAccountForm.securitiesList.filter((i) => i.check);
            const fundSelect = pState.selectAccountForm.fundList.filter((i) => i.check);
            const tradeSelect = pState.selectAccountForm.tradePermissionList.filter((i) => i.check);
            const needDerivativeTradePermission = resolveNeedDerivativeTradePermission(
              tradeSelect.length,
            );

            await saveAccount({
              securitiesAccountType: securSelect.map((i) => i.value).toString(),
              fundAccountType: fundSelect.map((i) => i.value).toString(),
              needDerivativeTradePermission,
              progress: 11,
            });

            await saveApply({
              needDerivativeTradePermission,
              applyType: 'NEW_APPLY',
            });

            pState.selectAccountForm.securitiesAccountType = securSelect.map((i) => i.value).toString();
            pState.selectAccountForm.fundAccountType = fundSelect.map((i) => i.value).toString();
            pState.selectAccountForm.needDerivativeTradePermission = needDerivativeTradePermission;
            nextProcess();
          }}
        />
      </If>
    </div>
  );
};
